import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  InsightsCaptureModel,
  SetSelectedProfilesPayload,
  SetInsightsPayload,
  SetOrganicInsightsPayload,
  SetEditInsightsPayload,
  OrganicInsight,
  SurveyAnswer,
  SetRemoveInsightPayload,
  TopicSubTopic,
  SetTopicSubTopic,
  Interaction,
  SetInteraction,
  SetParentActivityId,
  SetParentInteraction,
  NameValue,
  SetActivityInsightsPayload,
  SetAllOrganicInsightsPayload,
  SetExpansionPanel,
  SetTeamId,
  SetOrganicInsightKey,
  SetInsightSourcePayload,
} from '../../models/insights-capture.model';
import {
  addNewInsight,
  updateOrganicInsight,
  removeInsight,
} from '../../../modules/insights/components/capture/common/InsightsHelper';
import { convertToKwelloDate } from 'common/constants';

// identifier
export const INSIGHTS_CAPTURE_SLICE_ID = 'insightsCapture';

// default state
const initialState: InsightsCaptureModel = {
  selectedProfiles: <NameValue[]>[],
  insights: <SurveyAnswer[]>[],
  organicInsights: <OrganicInsight[]>[],
  organicInsightKey: 0,
  editInsight: null,
  topics: <TopicSubTopic[]>[],
  interaction: <Interaction>{},
  parentActivityId: <number>0,
  activityPanelExpansion: <string>'',
  teamId: <string>'',
  createdDate: convertToKwelloDate(new Date()),
  insightSourceId: null,
};

//slice
const insightsCaptureSlice = createSlice({
  name: INSIGHTS_CAPTURE_SLICE_ID,
  initialState,
  reducers: {
    setSelectedProfiles: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetSelectedProfilesPayload>
    ) => {
      state.selectedProfiles = action.payload.selectedProfiles;
    },
    addInsights: (state: InsightsCaptureModel, action: PayloadAction<SetInsightsPayload>) => {
      state.insights = addNewInsight({
        insights: state.insights,
        insight: action.payload.insights,
        additionalTrackingInfo: action.payload.additionalTrackingInfo,
      });
    },
    setInsights: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetActivityInsightsPayload>
    ) => {
      state.insights = action.payload.insights;
    },

    removeInsights: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetRemoveInsightPayload>
    ): void => {
      state.insights = removeInsight({ removeInsight: action.payload, insights: state.insights });
    },
    setOrganicInsightKey: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetOrganicInsightKey>
    ) => {
      state.organicInsightKey = action.payload.organicInsightKey;
    },
    editInsight: (state: InsightsCaptureModel, action: PayloadAction<SetEditInsightsPayload>) => {
      state.editInsight = action.payload.insight;
    },
    addOrganicInsights: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetOrganicInsightsPayload>
    ) => {
      state.organicInsights.push(action.payload.organicInsights);
    },
    setOrganicInsights: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetAllOrganicInsightsPayload>
    ) => {
      state.organicInsights = action.payload.organicInsights;
    },
    updateOrganicInsights: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetOrganicInsightsPayload>
    ) => {
      state.organicInsights = updateOrganicInsight({
        organicInsights: state.organicInsights,
        insight: action.payload.organicInsights,
      });
    },
    topics: (state: InsightsCaptureModel, action: PayloadAction<SetTopicSubTopic>): void => {
      state.topics = action.payload.topics;
    },
    interaction: (state: InsightsCaptureModel, action: PayloadAction<SetInteraction>): void => {
      state.interaction = action.payload.interaction;
    },
    setParentInteraction: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetParentInteraction>
    ): void => {
      state.interaction = {
        ...state.interaction,
        parentInteractionId: action.payload.parentInteractionId,
      };
    },
    clearActivity: (): InsightsCaptureModel => {
      return initialState;
    },
    setParentActivityId: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetParentActivityId>
    ): void => {
      state.parentActivityId = action.payload.parentActivityId;
    },
    setCreatedDate: (state: InsightsCaptureModel, action: PayloadAction<Date>) => {
      state.createdDate = convertToKwelloDate(action.payload);
    },
    setActivityPanelExpansion: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetExpansionPanel>
    ): void => {
      state.activityPanelExpansion = action.payload.activityPanelExpansion;
    },
    setTeamId: (state: InsightsCaptureModel, action: PayloadAction<SetTeamId>): void => {
      state.teamId = action.payload.teamId;
    },
    setInsightSource: (
      state: InsightsCaptureModel,
      action: PayloadAction<SetInsightSourcePayload>
    ): void => {
      state.insightSourceId = action.payload.insightSourceId;
    },
  },
});

//selectors
export const selectSelectedProfiles = (state: RootState) => state.insightsCapture.selectedProfiles;
export const selectInsights = (state: RootState) => state.insightsCapture.insights;
export const selectOrganicInsights = (state: RootState) => state.insightsCapture.organicInsights;
export const selectEditInsight = (state: RootState) => state.insightsCapture.editInsight;
export const selectTopics = (state: RootState) => state.insightsCapture.topics;
export const selectInteraction = (state: RootState) => state.insightsCapture.interaction;
export const selectParentActivityId = (state: RootState) => state.insightsCapture.parentActivityId;
export const selectCreatedDate = (state: RootState) => state.insightsCapture.createdDate;
export const selectInsightSourceId = (state: RootState) => state.insightsCapture.insightSourceId;

export const selectActivityExpandedPanel = (state: RootState) =>
  state.insightsCapture.activityPanelExpansion;
export const selectInsightsCaptureTeamId = (state: RootState) => state.insightsCapture.teamId;
export const selectOrganicInsightKey = (state: RootState) =>
  state.insightsCapture.organicInsightKey;

//actions
export const {
  setSelectedProfiles,
  addInsights,
  removeInsights,
  addOrganicInsights,
  updateOrganicInsights,
  editInsight,
  topics,
  interaction,
  clearActivity,
  setParentActivityId,
  setCreatedDate,
  setInsights,
  setOrganicInsights,
  setActivityPanelExpansion,
  setParentInteraction,
  setTeamId,
  setOrganicInsightKey,
  setInsightSource,
} = insightsCaptureSlice.actions;

//reducer
export default insightsCaptureSlice;
